import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Button, Drawer, Form, Input, Select, Space, Switch, Table, Tag, Typography, Modal, App, Popconfirm, } from 'antd';
import { PlusOutlined, EditOutlined, StopOutlined, KeyOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useAuthStore } from '@/lib/auth-store';
export function UsersPage() {
    const { message } = App.useApp();
    const qc = useQueryClient();
    const hasPerm = useAuthStore((s) => s.hasPerm);
    const [drawer, setDrawer] = useState({ open: false });
    const [rolesModal, setRolesModal] = useState({ open: false });
    const [form] = Form.useForm();
    const [rolesForm] = Form.useForm();
    const users = useQuery({
        queryKey: ['users'],
        queryFn: async () => (await api.get('/users')).data,
    });
    const roles = useQuery({
        queryKey: ['roles'],
        queryFn: async () => (await api.get('/roles')).data,
    });
    const createMut = useMutation({
        mutationFn: async (v) => (await api.post('/users', v)).data,
        onSuccess: () => {
            message.success('Pengguna dibuat');
            qc.invalidateQueries({ queryKey: ['users'] });
            setDrawer({ open: false });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const patchMut = useMutation({
        mutationFn: async ({ id, body }) => (await api.patch(`/users/${id}`, body)).data,
        onSuccess: () => {
            message.success('Perubahan disimpan');
            qc.invalidateQueries({ queryKey: ['users'] });
            setDrawer({ open: false });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const rolesMut = useMutation({
        mutationFn: async ({ id, roles: rr }) => (await api.post(`/users/${id}/roles`, { roles: rr })).data,
        onSuccess: () => {
            message.success('Role diperbarui');
            qc.invalidateQueries({ queryKey: ['users'] });
            setRolesModal({ open: false });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const deactivateMut = useMutation({
        mutationFn: async (id) => (await api.post(`/users/${id}/deactivate`)).data,
        onSuccess: () => {
            message.success('Pengguna dinonaktifkan');
            qc.invalidateQueries({ queryKey: ['users'] });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const columns = [
        { title: 'Username', dataIndex: 'username', width: 160 },
        { title: 'Nama Lengkap', dataIndex: 'full_name' },
        { title: 'Email', dataIndex: 'email', render: (e) => e || _jsx(Typography.Text, { type: "secondary", children: "\u2014" }) },
        {
            title: 'Role',
            dataIndex: 'roles',
            render: (rs) => (_jsx(Space, { size: 4, wrap: true, children: rs.map((r) => (_jsx(Tag, { color: "volcano", children: r }, r))) })),
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            width: 100,
            render: (a) => (a ? _jsx(Tag, { color: "success", children: "Aktif" }) : _jsx(Tag, { children: "Nonaktif" })),
        },
        {
            title: 'Login Terakhir',
            dataIndex: 'last_login_at',
            width: 180,
            render: (t) => (t ? formatDateTime(t) : '—'),
        },
        {
            title: 'Aksi',
            width: 240,
            render: (_, r) => (_jsxs(Space, { children: [hasPerm('user:edit') && (_jsx(Button, { size: "small", icon: _jsx(EditOutlined, {}), onClick: () => {
                            setDrawer({ open: true, row: r });
                            form.setFieldsValue({ ...r, password: undefined });
                        }, children: "Edit" })), hasPerm('role:manage') && (_jsx(Button, { size: "small", icon: _jsx(KeyOutlined, {}), onClick: () => {
                            setRolesModal({ open: true, row: r });
                            rolesForm.setFieldsValue({ roles: r.roles });
                        }, children: "Role" })), hasPerm('user:deactivate') && r.is_active && (_jsx(Popconfirm, { title: "Nonaktifkan pengguna?", description: "Pengguna tidak bisa login lagi setelah ini.", onConfirm: () => deactivateMut.mutate(r.id), children: _jsx(Button, { size: "small", danger: true, icon: _jsx(StopOutlined, {}) }) }))] })),
        },
    ];
    return (_jsxs("div", { children: [_jsxs(Space, { style: { marginBottom: 16, display: 'flex', justifyContent: 'space-between' }, children: [_jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: "Pengguna" }), hasPerm('user:create') && (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => {
                            form.resetFields();
                            setDrawer({ open: true });
                        }, children: "Tambah Pengguna" }))] }), _jsx(Table, { rowKey: "id", loading: users.isLoading, dataSource: users.data ?? [], columns: columns, size: "middle" }), _jsx(Drawer, { open: drawer.open, onClose: () => setDrawer({ open: false }), title: drawer.row ? `Edit: ${drawer.row.username}` : 'Tambah Pengguna', width: 520, destroyOnClose: true, extra: _jsx(Button, { type: "primary", onClick: () => {
                        form.validateFields().then((v) => {
                            if (drawer.row) {
                                patchMut.mutate({ id: drawer.row.id, body: v });
                            }
                            else {
                                createMut.mutate(v);
                            }
                        });
                    }, loading: createMut.isPending || patchMut.isPending, children: "Simpan" }), children: _jsxs(Form, { form: form, layout: "vertical", preserve: false, children: [!drawer.row && (_jsx(Form.Item, { name: "username", label: "Username", rules: [{ required: true }], children: _jsx(Input, { autoFocus: true }) })), _jsx(Form.Item, { name: "full_name", label: "Nama Lengkap", rules: [{ required: true }], children: _jsx(Input, {}) }), _jsx(Form.Item, { name: "email", label: "Email", rules: [{ type: 'email' }], children: _jsx(Input, {}) }), _jsx(Form.Item, { name: "password", label: drawer.row ? 'Password baru (opsional)' : 'Password', rules: [
                                { required: !drawer.row, message: 'Password wajib' },
                                { min: 8, message: 'Minimal 8 karakter' },
                            ], children: _jsx(Input.Password, {}) }), !drawer.row && (_jsx(Form.Item, { name: "roles", label: "Role Awal", initialValue: [], children: _jsx(Select, { mode: "multiple", options: (roles.data ?? []).map((r) => ({ value: r.code, label: `${r.code} — ${r.name}` })) }) })), drawer.row && (_jsx(Form.Item, { name: "is_active", label: "Aktif", valuePropName: "checked", children: _jsx(Switch, {}) }))] }) }), _jsx(Modal, { open: rolesModal.open, onCancel: () => setRolesModal({ open: false }), title: `Role: ${rolesModal.row?.username}`, onOk: () => {
                    rolesForm.validateFields().then((v) => rolesMut.mutate({ id: rolesModal.row.id, roles: v.roles }));
                }, confirmLoading: rolesMut.isPending, children: _jsx(Form, { form: rolesForm, layout: "vertical", children: _jsx(Form.Item, { name: "roles", label: "Role", children: _jsx(Select, { mode: "multiple", options: (roles.data ?? []).map((r) => ({ value: r.code, label: `${r.code} — ${r.name}` })) }) }) }) })] }));
}
